import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import {
  getSite,
  createRams,
  listSubcontractors,
  type Site,
} from '../lib/db';
import { uploadRams } from '../lib/storage';
import { TRADE_PRESETS, HAZARD_PRESETS } from '../data/ramsPresets';
import { DictateButton } from '../components/DictateButton';
import { LoadingIndicator } from '../components/LoadingIndicator';

export function RamsAdd() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const fileRef = useRef<HTMLInputElement>(null);
  const ramsIdRef = useRef<string>(crypto.randomUUID());

  const [site, setSite] = useState<Site | null>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [trade, setTrade] = useState('');
  const [task, setTask] = useState('');
  const [hazards, setHazards] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!siteId || !org) return;
    Promise.all([
      getSite(siteId),
      listSubcontractors(org.org_id),
    ])
      .then(([s, subs]) => {
        setSite(s);
        setCompanies(subs.map((sub) => sub.company_name));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [siteId, org]);

  function toggleHazard(h: string) {
    setHazards((prev) => prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h]);
  }

  // Allowed types for a RAMS upload — PDF, image, or Word doc. The Word
  // MIME types are checked explicitly because Office uses two different
  // values (.doc legacy = application/msword, .docx modern = the long
  // openxmlformats string).
  const RAMS_ALLOWED_MIME = /^(application\/pdf|image\/.+|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/i;

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError('File is over 10MB. Try compressing or splitting into pages.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (!RAMS_ALLOWED_MIME.test(f.type)) {
      setError('Only PDFs, Word documents and images are supported.');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFile(f);
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site) return;
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!file) { setError('Pick a PDF or photo of the RAMS.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const ramsId = ramsIdRef.current;
      const filePath = await uploadRams(org.org_id, ramsId, file);
      await createRams({
        id: ramsId,
        orgId: org.org_id,
        siteId: site.id,
        title: title.trim(),
        company: company.trim() || null,
        trade_preset: trade || null,
        task_preset: task.trim() || null,
        hazard_presets: hazards,
        notes: notes.trim() || null,
        file_url: filePath,
        file_name: file.name,
        mime_type: file.type || 'application/pdf',
        size_bytes: file.size,
      });
      // Land on the list view with ?share=<id> so the ShareModal auto-opens
      // and the user sees their newly-uploaded RAMS in context before
      // emailing it on.
      nav(`/sites/${site.id}/rams/list?share=${ramsId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return <AppShell title="Add RAMS" back={`/sites/${siteId ?? ''}/rams`}><LoadingIndicator /></AppShell>;
  }
  if (!site) {
    return <AppShell title="Add RAMS" back={`/sites/${siteId ?? ''}/rams`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;
  }

  return (
    <AppShell title="Upload RAMS" back={`/sites/${site.id}/rams`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          <strong>Already got a RAMS?</strong> Drop in the PDF the subbie has emailed you,
          or snap a photo of a printed copy. Stored against this site — viewable on iPad or phone
          for any HSE visit or audit.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="r-title">Title *</label>
          <div className="input-with-mic">
            <input
              id="r-title"
              type="text"
              required
              placeholder="e.g. ACME Scaffolding — tower for plot 1"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setTitle((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="r-company">Company</label>
          <input
            id="r-company"
            type="text"
            list="known-companies"
            placeholder="e.g. ACME Scaffolding"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <datalist id="known-companies">
            {companies.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>

        <div className="field">
          <label htmlFor="r-trade">Trade</label>
          <select id="r-trade" value={trade} onChange={(e) => setTrade(e.target.value)}>
            <option value="">Pick a trade…</option>
            {TRADE_PRESETS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>

        <div className="field">
          <label htmlFor="r-task">Task description</label>
          <div className="input-with-mic">
            <input
              id="r-task"
              type="text"
              placeholder="e.g. Erect tower scaffold to plot 1 east elevation"
              value={task}
              onChange={(e) => setTask(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setTask((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <div className="field">
          <label>Hazards covered (tap to toggle)</label>
          <div className="hazard-grid">
            {HAZARD_PRESETS.map((h) => (
              <button
                key={h}
                type="button"
                className={`hazard-chip ${hazards.includes(h) ? 'active' : ''}`}
                onClick={() => toggleHazard(h)}
              >
                {h}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="r-notes">Notes</label>
          <div className="input-with-mic">
            <textarea
              id="r-notes"
              rows={2}
              placeholder="e.g. Reviewed against TG20:21 — accepted"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <DictateButton onTranscript={(t) => setNotes((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <div className="field">
          <label>File *</label>
          <div className="card" style={{ textAlign: 'center', padding: 18 }}>
            <p className="muted" style={{ margin: '0 0 10px' }}>
              Upload a PDF or Word doc, pick a photo from your library, or take a new photo of a printed RAMS.
            </p>
            {/* No `capture` attribute — that would jump straight to the camera
                and skip the picker. Without it, iOS shows the full options:
                Photo Library, Take Photo, Browse (Files for PDFs / Word docs
                stored in iCloud, Dropbox, Google Drive, etc.). */}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={onFileChange}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileRef.current?.click()}
              style={{ width: 'auto', padding: '10px 16px' }}
            >
              {file ? 'Change file' : 'Upload file or photo'}
            </button>
            {file && (
              <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
                <strong>{file.name}</strong><br />
                {(file.size / 1024).toFixed(0)} KB · {file.type || 'application/pdf'}
              </p>
            )}
          </div>
        </div>

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting || !title.trim() || !file}>
            {submitting ? 'Uploading…' : 'Save to library'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/rams`)}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
